import { useState, useContext, Children } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { UserImageComponent } from '~/components/Chat/UserImageComponent';
import ImageBox from '~/components/ImageBox';
import { ArtyImageBox } from '~/components/arty/card';
import { ArtyRenderContext } from '~/components/Chat/artyRenderContext';
import ShowUpload from '~/components/ShowUpload';
import { DailySMSTipsSignup } from '~/components/DailySMSTipsSignup';
import { toTownSquareUrl } from '~/utils/townSquareLinks';

export function Result({ toolCall }: { toolCall: any }) {
  const { t } = useTranslation('chat');
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="my-2 mt-6 mb-4">
      <div
        className="flex items-center justify-between cursor-pointer select-none"
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="text-xs text-gray-500 font-medium">
          {t('tool.common.tool-completed')} <span className="text-gray-700 font-semibold">{toolCall.toolName}</span>
        </span>
        <ChevronRight
          className={`ml-2 w-3 h-3 text-gray-400 transition-transform duration-200 ${expanded ? 'rotate-90' : 'rotate-0'}`}
        />
      </div>
      {expanded && (
        <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-md">
          <p className="text-xs text-gray-500 mb-2">{t('tool.common.tool-result')}</p>
          <pre className="text-xs text-gray-700 font-mono whitespace-pre-wrap max-h-48 overflow-y-auto">
            {typeof toolCall.result === 'string' ? toolCall.result : JSON.stringify(toolCall.result, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

export function Calling({ text }: { text: string }) {
  return (
    <div className="flex items-center py-2">
      <div className="w-3 h-3 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin" />
      <span className="ml-3 text-xs text-gray-400">{text}</span>
    </div>
  );
}

export function TranslatedCalling({ i18nKey }: { i18nKey: string }) {
  const { t } = useTranslation('chat');
  return <Calling text={t(i18nKey)} />;
}

export function cleanUrl(url: string): string {
  const [baseUrl, ...queryParts] = url.split('?');
  const queryString = queryParts.join('?');
  const cleanedBaseUrl = baseUrl.replace(/([^:]\/)\/+/g, '$1');
  return queryString ? `${cleanedBaseUrl}?${queryString}` : cleanedBaseUrl;
}

const baseMarkdownComponents = {
  p: (props: any) => {
    // Check if this paragraph contains an image element
    const { children } = props;
    const containsImage = Children.toArray(children).some(
      (child: any) => child?.type === 'img' || (child?.props && (child.props.src || child.props.alt))
    );

    if (containsImage) {
      // Render as div instead of p to avoid HTML validation issues with block elements
      return <div className="mb-2 font-light text-base leading-7" {...props} />;
    }

    return <p className="mb-2 font-light text-base leading-7" {...props} />;
  },
  ul: (props: any) => (
    <ul className="list-disc ml-8 mb-3 font-light text-base leading-7 marker:font-bold marker:text-black" {...props} />
  ),
  ol: (props: any) => (
    <ol
      className="list-decimal ml-8 mb-3 font-light text-base leading-7 marker:font-bold marker:text-black"
      {...props}
    />
  ),
  li: (props: any) => <li className="my-2 font-light text-base leading-7" {...props} />,
  h1: (props: any) => <h1 className="text-3xl font-semibold mb-4 mt-6 leading-8" {...props} />,
  h2: (props: any) => <h2 className="text-2xl font-semibold mb-3 mt-6 leading-7" {...props} />,
  h3: (props: any) => <h3 className="text-xl font-semibold mb-3 mt-6 leading-7" {...props} />,
  h4: (props: any) => <h4 className="text-lg font-semibold mb-2 mt-6 leading-6" {...props} />,
  h5: (props: any) => <h5 className="text-base font-semibold mb-2 mt-6 leading-6" {...props} />,
  h6: (props: any) => <h6 className="text-sm font-semibold mb-2 mt-6 leading-5" {...props} />,
  blockquote: (props: any) => (
    <blockquote
      className="border-l-4 border-gray-200 pl-4 py-2 my-4 italic mb-3 font-light text-base leading-7"
      {...props}
    />
  ),
  hr: (props: any) => <hr className="my-4" {...props} />,
  a: (props: any) => {
    const { href, ...rest } = props;
    const className = 'text-blue-500 font-light text-base leading-7';

    // Town-square routes (e.g. /community, /contests, /verify) live in a different
    // app and would 404 if routed through React Router. Render as a full-page <a>
    // pointing at the town-square origin so navigation crosses the domain cleanly.
    // toTownSquareUrl returns null only for non-town-square paths; when
    // __TOWN_SQUARE_URL__ is unset it falls back to DEFAULT_TOWN_SQUARE_URL
    // (https://www.arthelper.com) — same pattern as AppSidebar / SiteHeader.
    const townSquareUrl = toTownSquareUrl(href, __TOWN_SQUARE_URL__);
    if (townSquareUrl) {
      return <a href={townSquareUrl} target="_blank" rel="noopener noreferrer" className={className} {...rest} />;
    }

    // Other relative URLs are client-app routes — keep React Router <Link>.
    if (href && href.startsWith('/')) {
      return <Link to={href} target="_blank" rel="noopener noreferrer" className={className} {...rest} />;
    }

    // External link - use regular anchor tag
    return <a href={href} target="_blank" rel="noopener noreferrer" className={className} {...rest} />;
  },
  strong: (props: any) => <strong className="font-semibold" {...props} />,
  // Code block components
  pre: (props: any) => <pre className="my-4 overflow-x-auto whitespace-pre-wrap break-words" {...props} />,
  code: (props: any) => <code className="text-sm font-mono" {...props} />,
  // Table components
  table: (props: any) => (
    <div className="my-4 mb-12 grid grid-cols-1 w-full min-w-0 overflow-x-auto">
      <table className="border-collapse text-xs [&_tbody_tr:last-child]:border-b-0 min-w-full col-start-1" {...props} />
    </div>
  ),
  thead: (props: any) => <thead {...props} />,
  tbody: (props: any) => <tbody {...props} />,
  tr: (props: any) => <tr className="border-b border-gray-200" {...props} />,
  th: (props: any) => <th className="p-3 text-left font-semibold text-gray-900 min-w-30 max-w-48" {...props} />,
  td: (props: any) => <td className="p-3 font-light text-gray-700 first:font-semibold min-w-30 max-w-48" {...props} />,
  ShowUpload: (props: any) => <ShowUpload {...props} />,
  DailySMSTipsSignup: (props: any) => <DailySMSTipsSignup {...props} />,
};

/** A markdown `![alt](src)` image. In the flyout it is the Arty card, same as an `<ImageBox />` tag. */
function MarkdownImage(props: any) {
  const { card } = useContext(ArtyRenderContext);
  if (card) {
    return <ArtyImageBox imageId={props.imageId} src={props.src} alt={props.alt} />;
  }
  return <ImageBox {...props} />;
}

export const markdownComponents = {
  ...baseMarkdownComponents,
  img: MarkdownImage,
  ImageBox: MarkdownImage,
};

export const publicMarkdownComponents = {
  ...baseMarkdownComponents,
  img: (props: any) => <UserImageComponent {...props} />,
  ImageBox: (props: any) => <UserImageComponent {...props} />,
};
